import React, { useState } from 'react';
import QRCode from 'qrcode';
import { generarQR } from '../api';
import { translateError } from '../utils';

export default function QRGenerator({ onBack }) {
  const [form, setForm] = useState({ motivo: '', fecha_caducidad: '' });
  const [visitantes, setVisitantes] = useState([{ nombre: '', cedula: '' }]);
  const [placas, setPlacas] = useState([]);
  const [qrImage, setQrImage] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleVisitanteChange = (index, field, value) => {
    const updated = [...visitantes];
    updated[index][field] = value;
    setVisitantes(updated);
  };

  const addVisitante = () => {
    if (visitantes.length >= 10) return;
    setVisitantes([...visitantes, { nombre: '', cedula: '' }]);
  };

  const removeVisitante = (index) => {
    if (visitantes.length <= 1) return;
    setVisitantes(visitantes.filter((_, i) => i !== index));
  };

  const addPlaca = () => {
    if (placas.length >= 5) return;
    setPlacas([...placas, '']);
  };

  const handlePlacaChange = (index, value) => {
    const updated = [...placas];
    updated[index] = value;
    setPlacas(updated);
  };

  const removePlaca = (index) => {
    setPlacas(placas.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const primerVisitante = visitantes[0];
    if (!primerVisitante.nombre.trim() || !primerVisitante.cedula.trim()) {
      setError('El nombre y cédula del visitante principal son obligatorios');
      return;
    }
    setLoading(true);
    try {
      const data = await generarQR({
        ...form,
        visitantes: visitantes.filter(v => v.nombre.trim() && v.cedula.trim()),
        placas: placas.filter(p => p.trim())
      });
      const qrJson = JSON.stringify({
        id: data.id, token: data.token,
        visitantes: data.visitantes,
        placas: data.placas,
        motivo: data.motivo,
        fecha_caducidad: data.fecha_caducidad,
        residente_nombre: data.residente_nombre,
        apartamento: data.apartamento, piso: data.piso
      });
      const img = await QRCode.toDataURL(qrJson, { width: 400, margin: 2 });
      setQrImage(img);
      setQrData(data);
    } catch (err) { setError(translateError(err)); }
    setLoading(false);
  };

  const handleShare = async () => {
    if (navigator.share && qrImage) {
      try {
        const res = await fetch(qrImage);
        const blob = await res.blob();
        await navigator.share({
          title: 'QR de Visita',
          text: `Visita - Motivo: ${form.motivo}`,
          files: [new File([blob], 'qr-visita.png', { type: 'image/png' })]
        });
      } catch {
        await navigator.clipboard.writeText(`Visita - Vence: ${form.fecha_caducidad}`);
        alert('📋 Información copiada');
      }
    } else {
      const link = document.createElement('a');
      link.href = qrImage;
      link.download = 'qr-visita.png';
      link.click();
    }
  };

  if (qrImage && qrData) {
    return (
      <div className="main-content">
        <div className="page-header flex-between">
          <div><h1>QR Generado Exitosamente</h1><p>Comparte este código con tus visitantes</p></div>
          <button className="btn btn-outline" onClick={() => { setQrImage(null); setQrData(null); }}>Generar otro</button>
        </div>
        <div className="card" style={{ maxWidth: '640px' }}>
          <div className="card-header"><h2>✅ Pase de Visita</h2><span className="badge badge-info">Activo</span></div>
          <div className="qr-container"><img src={qrImage} alt="QR de visita" /></div>
          <div className="qr-info">
            {qrData.visitantes.map((v, i) => (
              <p key={i}><strong>👤 Visitante {i + 1}</strong> {v.nombre}<br/><span style={{fontSize:'0.78rem',color:'var(--text-secondary)'}}>🪪 {v.cedula}</span></p>
            ))}
            <p><strong>🚗 Vehículos</strong> {qrData.placas && qrData.placas.length > 0 ? qrData.placas.join(', ') : 'N/A'}</p>
            <p><strong>📋 Motivo</strong> {qrData.motivo}</p>
            <p><strong>📅 Caduca</strong> {qrData.fecha_caducidad}</p>
          </div>
          <div className="btn-row mt-16">
            <button className="btn btn-accent" onClick={handleShare}>📤 Compartir QR</button>
            <button className="btn btn-outline" onClick={onBack}>🏠 Volver al menú</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header flex-between">
        <div><h1>Generar QR de Visita</h1><p>Crea un pase digital para tus visitantes</p></div>
        <button className="btn btn-outline" onClick={onBack}>Volver</button>
      </div>
      <div className="card" style={{ maxWidth: '620px' }}>
        <div className="card-header"><h2>📱 Datos de la visita</h2></div>
        {error && <div className="alert alert-error">⚠️ {error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="mb-16">
            <div className="flex-between mb-8">
              <label style={{fontWeight:700,fontSize:'0.78rem',color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:'0.8px'}}>
                👥 Visitantes ({visitantes.length})
              </label>
              {visitantes.length < 10 && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={addVisitante}>+ Añadir persona</button>
              )}
            </div>
            {visitantes.map((v, i) => (
              <div key={i} style={{background:'rgba(99,102,241,0.03)',borderRadius:'var(--radius-sm)',padding:'14px',marginBottom:'10px',border:'1px solid var(--border-light)'}}>
                <div className="flex-between mb-8">
                  <span style={{fontWeight:600,fontSize:'0.82rem',color:'var(--primary)'}}>Visitante {i + 1} {i === 0 && '(principal)'}</span>
                  {i > 0 && <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeVisitante(i)} style={{color:'var(--danger)'}}>✕ Quitar</button>}
                </div>
                <div className="form-row">
                  <div className="form-group" style={{marginBottom:0}}>
                    <label>Nombre completo *</label>
                    <input value={v.nombre} onChange={(e) => handleVisitanteChange(i, 'nombre', e.target.value)} placeholder="Nombre del visitante" required={i === 0} />
                  </div>
                  <div className="form-group" style={{marginBottom:0}}>
                    <label>Cédula *</label>
                    <input value={v.cedula} onChange={(e) => handleVisitanteChange(i, 'cedula', e.target.value)} placeholder="Número de cédula" required={i === 0} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mb-16">
            <div className="flex-between mb-8">
              <label style={{fontWeight:700,fontSize:'0.78rem',color:'var(--text-secondary)',textTransform:'uppercase',letterSpacing:'0.8px'}}>
                🚗 Vehículos ({placas.length})
              </label>
              {placas.length < 5 && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={addPlaca}>+ Añadir vehículo</button>
              )}
            </div>
            {placas.length === 0 && (
              <p style={{color:'var(--text-secondary)',fontSize:'0.82rem',marginBottom:'10px'}}>Sin vehículos registrados</p>
            )}
            {placas.map((placa, i) => (
              <div key={i} style={{background:'rgba(245,158,11,0.03)',borderRadius:'var(--radius-sm)',padding:'10px 14px',marginBottom:'8px',border:'1px solid var(--border-light)',display:'flex',alignItems:'center',gap:'10px'}}>
                <span style={{fontWeight:600,fontSize:'0.82rem',color:'var(--accent)',whiteSpace:'nowrap'}}>🚗 Vehículo {i + 1}</span>
                <input
                  value={placa}
                  onChange={(e) => handlePlacaChange(i, e.target.value)}
                  placeholder="Número de placa"
                  style={{flex:1}}
                />
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => removePlaca(i)} style={{color:'var(--danger)',padding:'4px 8px'}}>✕</button>
              </div>
            ))}
          </div>
          <div className="form-row">
            <div className="form-group"><label>Fecha de caducidad del QR *</label><input type="date" name="fecha_caducidad" value={form.fecha_caducidad} onChange={handleChange} required min={new Date().toISOString().split('T')[0]} /></div>
          </div>
          <div className="form-group">
            <label>Motivo de la visita *</label>
            <select name="motivo" value={form.motivo} onChange={handleChange} required>
              <option value="">Seleccionar motivo...</option>
              <option value="Visita familiar">Visita familiar</option>
              <option value="Entrega / Domicilio">Entrega / Domicilio</option>
              <option value="Mantenimiento">Mantenimiento</option>
              <option value="Reunión social">Reunión social</option>
              <option value="Otro">Otro</option>
            </select>
          </div>
          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? 'Generando...' : '🔲 Generar Código QR'}
          </button>
        </form>
      </div>
    </div>
  );
}